import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import { Modal, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import config from '../config/config';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      const id = localStorage.getItem('userId');
      const email = localStorage.getItem('email');
      const first_name = localStorage.getItem('first_name');
      const last_name = localStorage.getItem('last_name');
      const role = localStorage.getItem('role');
      setUser({ id, email, first_name, last_name, role });
      setToken(storedToken);
    }
    setLoading(false);

    const handleStorageChange = (event) => {
      if (event.key === 'token' && event.oldValue && !event.newValue) {
        Modal.warning({
            title: 'Zostałeś wylogowany w innej karcie',
            content: 'Zaloguj się ponownie.',
            onOk: () => {
              logout();
            },
          });
      }
    };

    checkAuth();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async (values, navigate) => {
    try {
      const response = await axiosInstance.post('/auth/login', {
        email: values.email,
        password: values.password,
      });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('userId', user.id);
      localStorage.setItem('email', user.email);
      localStorage.setItem('first_name', user.first_name);
      localStorage.setItem('last_name', user.last_name);
      localStorage.setItem('role', user.role);
      localStorage.setItem('roleId', user.roleId);
      setUser({
        id: user.id,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        roleId: user.roleId,
      });
      setToken(token);
      console.log('Login successful, navigating to dashboard');
      console.log('User set:', user);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed', error);
      message.error('Nieprawidłowy email lub hasło.');
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    localStorage.removeItem('userId');
    localStorage.removeItem('role');
    localStorage.removeItem('roleId');
    setUser(null);
    setToken(null);
    console.log('User set to null');
    navigate('/login');
  }, [navigate]);

  const checkAuth = async () => {
    try {
      const response = await axiosInstance.get('/auth/verify-token', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      }); 
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      await axiosInstance.post('/auth/request-password-reset', { email });
      message.success('E-mail z linkiem resetu został wysłany');
    } catch (error) {
      console.error('Password reset request failed', error);
      message.error('Wystąpił błąd podczas wysyłania prośby o reset hasła.');
      throw error;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axiosInstance.post(`/auth/reset-password/${token}`, { newPassword });
      message.success('Hasło zostało zmienione pomyślnie');
      navigate('/login');
    } catch (error) {
      // Sprawdź, czy mamy do czynienia z wygasłym tokenem
      if (error.response?.data?.tokenExpired) {
        message.error(error.response.data.message);
        // Przekieruj do strony logowania po krótkim opóźnieniu
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        message.error('Wystąpił błąd podczas resetowania hasła');
      }
    }
  };

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          Modal.warning({
            title: 'Zostałeś automatycznie wylogowany',
            content: 'Zaloguj się ponownie.',
            onOk: () => {
              logout();
            },
          });
        }
        return Promise.reject(error);
      }
    );
    if(user) checkAuth();

    return () => {
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, requestPasswordReset, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

